import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';

const API_BASE_URL = config.apiBaseUrl;

export interface DocumentData {
  id: string;
  title: string;
  url: string;
  type: string;
  size: string;
  lastModified: string;
  status: string;
  content?: string;
  binaryData?: ArrayBuffer;
}

export interface DocumentPreviewInfo {
  isPreview: boolean;
  totalPages: number;
  previewPages: number;
  previewMessage: string;
}

export interface Annotation {
  id: string;
  text: string;
  position: { x: number; y: number };
  author: string;
  timestamp: string;
  compId: string;
}

export interface Note {
  id: string;
  title?: string;
  content: string;
  author: string;
  timestamp: string;
  compId: string;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper para obtener el userId del usuario autenticado
  private getUserIdParam(): string {
    try {
      // NOTA: Esto solo funciona en componentes React, pero aquí es un workaround global
      // En producción, deberías pasar el userId explícitamente o usar un singleton/context global
      const auth = (window as any).auritasAuthContext;
      if (auth && auth.user && auth.user.username) {
        // Puedes cambiar esto por el campo que sea el ID real
        return `&userId=${encodeURIComponent(auth.user.username)}`;
      }
    } catch {}
    return '';
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Agregar userId automáticamente si está disponible
    const userIdParam = this.getUserIdParam();
    // Si la URL ya tiene userId, no lo agregues de nuevo
    const url = `${this.baseUrl}${endpoint}${endpoint.includes('userId=') ? '' : userIdParam}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document not found');
        }
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // Check if response is binary data
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/octet-stream')) {
        const arrayBuffer = await response.arrayBuffer();
        return arrayBuffer as unknown as T;
      }

      // Check if response is PDF (for document data)
      if (contentType && contentType.includes('application/pdf')) {
        const arrayBuffer = await response.arrayBuffer();
        return arrayBuffer as unknown as T;
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Parse document-viewer URL to extract all query parameters
  parseDocumentViewerUrl(url: string): string | null {
    try {
      console.log('🔍 Parsing document viewer URL:', url);
      
      const urlObj = new URL(url);
      
      // Verificar si es una URL con XML (contiene parámetro 'xml')
      if (urlObj.searchParams.has('xml')) {
        console.log('📄 Detected XML URL format');
        
        try {
          const xmlParam = urlObj.searchParams.get('xml');
          if (!xmlParam) {
            console.error('❌ No XML parameter found');
            return null;
          }
          
          // Decodificar el XML
          const xmlContent = decodeURIComponent(xmlParam);
          console.log('📄 Decoded XML:', xmlContent);
          
          // Parsear el XML usando DOMParser
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
          
          // Verificar si hay errores de parsing
          const parseError = xmlDoc.querySelector('parsererror');
          if (parseError) {
            console.error('❌ XML parsing error:', parseError.textContent);
            return null;
          }
          
          // Extraer datos del documento
          const docElement = xmlDoc.querySelector('doc');
          if (!docElement) {
            console.error('❌ No doc element found in XML');
            return null;
          }
          
          const docUrl = docElement.getAttribute('url');
          if (!docUrl) {
            console.error('❌ No URL found in doc element');
            return null;
          }
          
          // Parsear la URL del documento para extraer parámetros
          const docUrlObj = new URL(docUrl);
          const docParams = new URLSearchParams(docUrlObj.search);
          
          // Construir query string con los parámetros extraídos
          const queryParams = new URLSearchParams();
          queryParams.set('docId', docParams.get('docId') || '');
          queryParams.set('contRep', docParams.get('contRep') || '');
          queryParams.set('accessMode', docParams.get('accessMode') || '');
          queryParams.set('authId', docParams.get('authId') || '');
          queryParams.set('expiration', docParams.get('expiration') || '');
          queryParams.set('secKey', docParams.get('secKey') || '');
          queryParams.set('pVersion', docParams.get('pVersion') || '');
          
          // Agregar userId si está disponible
          const userElement = xmlDoc.querySelector('user');
          const clientUser = userElement?.getAttribute('clientUser');
          if (clientUser) {
            queryParams.set('userId', clientUser);
          }
          
          const queryString = queryParams.toString();
          console.log('✅ Built API query string from XML:', queryString);
          return `?${queryString}`;
          
        } catch (xmlError) {
          console.error('❌ Error parsing XML:', xmlError);
          return null;
        }
      }
      
      // Formato tradicional - extraer query string directamente
      console.log('📄 Traditional URL format detected');
      return urlObj.search;
    } catch (error) {
      console.error('❌ Error parsing document viewer URL:', error);
      return null;
    }
  }

  // Get document metadata by using info command
  async getDocumentMetadata(queryParams: string): Promise<any> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?info&${params.toString()}`;
    return this.makeRequest<any>(endpoint);
  }

  // Get document data by adding compId=data to the query parameters (only get)
  async getDocumentData(queryParams: string): Promise<ArrayBuffer> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?get&${params.toString()}&compId=data`;
    return this.makeRequest<ArrayBuffer>(endpoint);
  }

  // Get document binary content by adding compId=data to the query parameters
  async getDocumentBinary(queryParams: string): Promise<{ data: ArrayBuffer; previewInfo?: DocumentPreviewInfo }> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=data`;
    
    // Agregar userId automáticamente si está disponible
    const userIdParam = this.getUserIdParam();
    const url = `${this.baseUrl}${endpoint}${endpoint.includes('userId=') ? '' : userIdParam}`;
    
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/octet-stream',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Document not found');
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    
    // Check for preview headers
    const isPreview = response.headers.get('X-Document-Preview') === 'true';
    let previewInfo: DocumentPreviewInfo | undefined;
    
    if (isPreview) {
      previewInfo = {
        isPreview: true,
        totalPages: parseInt(response.headers.get('X-Total-Pages') || '0'),
        previewPages: parseInt(response.headers.get('X-Preview-Pages') || '0'),
        previewMessage: response.headers.get('X-Preview-Message') || 'Preview mode'
      };
    }
    
    return { data: arrayBuffer, previewInfo };
  }

  // Get annotations (only get)
  async getAnnotations(queryParams: string): Promise<Annotation[]> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?get&${params.toString()}&compId=anno`;
    
    try {
      // Try to get as raw text first to handle concatenated JSON objects
      const rawResponse = await this.makeRawRequest(endpoint);
      console.log('🔷 Raw annotations response:', rawResponse);
      
      if (!rawResponse.trim()) {
        return [];
      }
      
      // Parse concatenated JSON objects
      const annotations: Annotation[] = [];
      let currentIndex = 0;
      
      while (currentIndex < rawResponse.length) {
        try {
          // Find the next complete JSON object
          let braceCount = 0;
          let startIndex = currentIndex;
          let endIndex = currentIndex;
          
          for (let i = currentIndex; i < rawResponse.length; i++) {
            if (rawResponse[i] === '{') {
              if (braceCount === 0) startIndex = i;
              braceCount++;
            } else if (rawResponse[i] === '}') {
              braceCount--;
              if (braceCount === 0) {
                endIndex = i + 1;
                break;
              }
            }
          }
          
          if (endIndex > startIndex) {
            const jsonStr = rawResponse.substring(startIndex, endIndex);
            const annotation = JSON.parse(jsonStr);
            annotations.push(annotation);
            currentIndex = endIndex;
          } else {
            break;
          }
        } catch (error) {
          console.error('🔷 Error parsing annotation JSON:', error);
          break;
        }
      }
      
      console.log('🔷 Parsed annotations:', annotations);
      return annotations;
    } catch (error) {
      console.error('🔷 Error getting annotations:', error);
      return [];
    }
  }

  // Create new annotation
  async createAnnotation(queryParams: string, annotation: Omit<Annotation, 'id' | 'compId' | 'timestamp'>): Promise<Annotation> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=anno`;
    return this.makeRequest<Annotation>(endpoint, {
      method: 'POST',
      body: JSON.stringify(annotation),
    });
  }

  // Update annotation
  async updateAnnotation(queryParams: string, annotationId: string, annotation: Partial<Annotation>): Promise<Annotation> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=anno&annotationId=${annotationId}`;
    return this.makeRequest<Annotation>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(annotation),
    });
  }

  // Delete annotation
  async deleteAnnotation(queryParams: string, annotationId: string): Promise<void> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=anno&annotationId=${annotationId}`;
    return this.makeRequest<void>(endpoint, {
      method: 'DELETE',
    });
  }

  // Delete all annotations (like notes)
  async deleteAllAnnotations(queryParams: string): Promise<void> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?delete&${params.toString()}&compId=anno`;
    const response = await fetch(`${this.baseUrl}${endpoint}`, { method: 'GET' });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete annotations: ${response.status}`);
    }
    // 404 is normal when there are no annotations to delete
  }

  // Append annotation (like notes)
  async appendAnnotation(queryParams: string, annotation: Omit<Annotation, 'id' | 'compId' | 'timestamp'>): Promise<void> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?append&${params.toString()}&compId=anno`;
    
    console.log('🔷 Append annotation endpoint:', endpoint);
    console.log('🔷 Append annotation data:', annotation);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(annotation),
    });
    
    console.log('🔷 Append annotation response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🔷 Append annotation error response:', errorText);
      throw new Error(`Failed to append annotation: ${response.status} - ${errorText}`);
    }
    
    console.log('🔷 Append annotation successful');
  }

  // Get notes (only get)
  async getNotes(queryParams: string): Promise<Note[]> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?get&${params.toString()}&compId=note`;
    return this.makeRequest<Note[]>(endpoint);
  }

  // Create new note
  async createNote(queryParams: string, note: Omit<Note, 'id' | 'compId' | 'timestamp'>): Promise<Note> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=note`;
    return this.makeRequest<Note>(endpoint, {
      method: 'POST',
      body: JSON.stringify(note),
    });
  }

  // Update note
  async updateNote(queryParams: string, noteId: string, note: Partial<Note>): Promise<Note> {
    // Remove the leading ? and any existing 'get' parameter
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    
    // Remove any existing 'get' parameter to avoid duplication
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    
    const endpoint = `/api/sap?get&${params.toString()}&compId=note&noteId=${noteId}`;
    return this.makeRequest<Note>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(note),
    });
  }

  // Delete a note by index: fetch all, remove one, re-upload
  async deleteNote(queryParams: string, noteIndex: number): Promise<void> {
    // Get all notes as raw text
    const allNotesRaw = await this.getNotesRaw(queryParams);
    // Parse notes into blocks (two lines per note, skipping empty lines)
    const lines = allNotesRaw.split(/\r?\n/).filter(l => l.trim() !== '');
    const notes: string[] = [];
    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i]) {
        const block = [lines[i]];
        if (lines[i + 1]) block.push(lines[i + 1]);
        notes.push(block.join('\n'));
      }
    }
    // Remove the note at noteIndex
    if (noteIndex >= 0 && noteIndex < notes.length) {
      notes.splice(noteIndex, 1);
    }
    // Re-upload all remaining notes (overwrite)
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const deleteEndpoint = `/api/sap?delete&${params.toString()}&compId=note`;
    await fetch(`${this.baseUrl}${deleteEndpoint}`, { method: 'GET' });
    for (const note of notes) {
      if (note.trim()) {
        const appendEndpoint = `/api/sap?append&${params.toString()}&compId=note`;
        await fetch(`${this.baseUrl}${appendEndpoint}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'text/plain' },
          body: note + '\n',
        });
      }
    }
  }

  // Fetch raw info (metadata) as plain text
  async getDocumentMetadataRaw(queryParams: string): Promise<string> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?info&${params.toString()}`;
    return this.makeRawRequest(endpoint);
  }

  // Fetch raw notes as plain text
  async getNotesRaw(queryParams: string): Promise<string> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?get&${params.toString()}&compId=note`;
    return this.makeRawRequest(endpoint);
  }

  // Helper for raw text requests
  private async makeRawRequest(endpoint: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}${endpoint}`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return response.text();
  }

  // Append a note to the SAP note component
  async appendNote(queryParams: string, noteBody: string): Promise<void> {
    const cleanParams = queryParams.startsWith('?') ? queryParams.substring(1) : queryParams;
    const params = new URLSearchParams(cleanParams);
    params.delete('get');
    params.delete('info');
    const endpoint = `/api/sap?append&${params.toString()}&compId=note`;
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/plain' },
      body: noteBody,
    });
    if (!response.ok) {
      throw new Error(`Failed to append note: ${response.status}`);
    }
  }
}

export const apiService = new ApiService();
export default apiService; 